import React, { useState, useEffect, Fragment, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  createReportVariableMeasurement as createReportVariableMeasurementApi,
  fetchReportVariableMeasurements,
} from "../../../../api/service/report-variables";
import { formatUnitDisplay } from "../../../../utils/reportVariableUnits";
import { intlLocaleFor } from "../../../../i18n/languages";
import {
  RV_DIALOG_Z,
  RV_OVERLAY_CLASS,
  RV_CENTER_WRAPPER,
  RV_CENTER_INNER,
  rvTransitionOverlay,
  rvTransitionPanel,
  RV_PANEL_LG,
  RV_DIALOG_TITLE,
  RV_INPUT,
  RV_BTN_OUTLINE,
  RV_BTN_OUTLINE_SM,
  RV_SECTION,
  RV_CLOSE_BTN,
} from "./report-variable-dialog-theme";

const VariableMeasurementDialog = ({
  open,
  onClose,
  variable,
  report,
  canEdit,
  mutations,
  collaborationAnchor,
  onMeasurementAdded,
}) => {
  const { t, i18n } = useTranslation();
  const createReportVariableMeasurement = mutations?.createReportVariableMeasurement || createReportVariableMeasurementApi;

  const [measurements, setMeasurements] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [newMeasurementValue, setNewMeasurementValue] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const measurementUnit =
    variable?.measurement_unit ||
    (variable?.type_group_id && variable?.type_group?.measurement_unit) ||
    variable?.variable_type_group?.measurement_unit;

  const projectedUnit =
    variable?.projected_unit || variable?.type_group?.projected_unit || null;

  const loadMeasurements = useCallback(async () => {
    if (!variable?.id || !report?.id) return;

    setIsLoadingHistory(true);
    try {
      const response = await fetchReportVariableMeasurements(report.id, variable.id);
      if (response.data) {
        const sorted = Array.isArray(response.data)
          ? response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          : [];
        setMeasurements(sorted);
      }
    } catch (error) {
      console.error("Error loading measurements:", error);
      toast.error(
        t("report.variables.sketchEditor.historyLoadError", { defaultValue: "Kunne ikke laste målinger" })
      );
    } finally {
      setIsLoadingHistory(false);
    }
  }, [variable?.id, report?.id, t]);

  useEffect(() => {
    if (open && variable?.id && report?.id) {
      loadMeasurements();
    } else {
      setMeasurements([]);
      setNewMeasurementValue("");
    }
  }, [open, variable?.id, report?.id, loadMeasurements]);

  const handleAddMeasurement = async () => {
    if (!variable?.id || !report?.id) return;

    const value = parseFloat(String(newMeasurementValue).replace(",", "."));
    if (Number.isNaN(value) || value <= 0) {
      toast.error(
        t("report.variables.sketchEditor.measurementInvalid", {
          defaultValue: "Oppgi en gyldig målingsverdi",
        })
      );
      return;
    }

    setIsSaving(true);
    try {
      const response = await createReportVariableMeasurement(report.id, variable.id, {
        value: value,
      });

      if (response.data) {
        toast.success(
          t("report.variables.sketchEditor.measurementAdded", { defaultValue: "Måling lagt til" })
        );
        setNewMeasurementValue("");
        await loadMeasurements();
        if (onMeasurementAdded) {
          onMeasurementAdded();
        }
      } else {
        toast.error(
          response.errors?.[0] ||
            t("report.variables.sketchEditor.measureError", { defaultValue: "Kunne ikke lagre måling" })
        );
      }
    } catch (error) {
      if (mutations) return;
      console.error("Error adding measurement:", error);
      toast.error(
        t("report.variables.sketchEditor.measureError", { defaultValue: "Kunne ikke lagre måling" })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const locale = intlLocaleFor(i18n.language);
    return new Date(dateString).toLocaleString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const latestMeasurement = measurements.length > 0 ? measurements[0] : null;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className={RV_DIALOG_Z} onClose={onClose}>
        <Transition.Child as={Fragment} {...rvTransitionOverlay}>
          <div className={RV_OVERLAY_CLASS} />
        </Transition.Child>

        <div className={RV_CENTER_WRAPPER}>
          <div className={RV_CENTER_INNER}>
            <Transition.Child as={Fragment} {...rvTransitionPanel}>
              <Dialog.Panel className={`${RV_PANEL_LG} !transform-none`}>
                <div ref={collaborationAnchor} />
                <div className="mb-5 flex items-start justify-between gap-3 border-b border-gray-200 pb-4">
                  <Dialog.Title className={`min-w-0 flex-1 ${RV_DIALOG_TITLE}`}>
                    {t("report.variables.measurementDialog.title", {
                      defaultValue: "Målinger",
                    })}{" "}
                    —{" "}
                    {variable?.reference ||
                      t("report.variables.measurementDialog.variableFallback", { defaultValue: "Variabel" })}
                  </Dialog.Title>
                  <button type="button" className={RV_CLOSE_BTN} onClick={onClose} aria-label="Close">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {variable && (
                  <div className={`mb-5 space-y-3 ${RV_SECTION}`}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("report.variables.measurementDialog.contextHeading", {
                        defaultValue: "Grunnlag",
                      })}
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium text-gray-600">
                        {t("report.variables.measurementDialog.reference", { defaultValue: "Referanse" })}:
                      </span>{" "}
                      <span className="text-gray-900">{variable.reference || "—"}</span>
                    </div>
                    {measurementUnit ? (
                      <div className="text-xs text-gray-500">
                        {t("report.variables.measurementDialog.measurementUnit", { defaultValue: "Måleenhet" })}:{" "}
                        {formatUnitDisplay(measurementUnit)}
                      </div>
                    ) : null}
                    {projectedUnit ? (
                      <div className="text-xs text-gray-500">
                        {t("report.variables.measurementDialog.projectedUnit", { defaultValue: "Prosjektert enhet" })}:{" "}
                        {formatUnitDisplay(projectedUnit)}
                      </div>
                    ) : null}
                    <div className="grid gap-3 sm:grid-cols-2">
                      {variable?.projected_quantity != null &&
                      String(variable.projected_quantity).trim() !== "" &&
                      projectedUnit ? (
                        <div className="rounded-2xl border border-gray-200 bg-white p-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {t("report.variables.measurementDialog.projectedLabel", {
                              defaultValue: "Prosjektert",
                            })}
                          </div>
                          <div className="mt-1 break-all text-xl font-semibold tabular-nums text-gray-900">
                            {formatUnitDisplay(projectedUnit, variable.projected_quantity)}
                          </div>
                        </div>
                      ) : null}
                      {variable?.pre_measurement_quantity != null &&
                      String(variable.pre_measurement_quantity).trim() !== "" &&
                      measurementUnit ? (
                        <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                            {t("report.variables.measurementDialog.preMeasurementLabel", {
                              defaultValue: "Før-måling",
                            })}
                          </div>
                          <div className="mt-1 break-all text-xl font-semibold tabular-nums text-amber-950">
                            {formatUnitDisplay(measurementUnit, variable.pre_measurement_quantity)}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {latestMeasurement ? (
                  <div className={`mb-5 ${RV_SECTION}`}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("report.variables.measurementDialog.latestHeading", { defaultValue: "Siste måling" })}
                    </h3>
                    <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                      {measurementUnit
                        ? formatUnitDisplay(measurementUnit, latestMeasurement.value)
                        : `${parseFloat(latestMeasurement.value).toFixed(2)}`}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{formatDate(latestMeasurement.created_at)}</div>
                  </div>
                ) : null}

                {canEdit ? (
                  <div className="mb-5 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("report.variables.sketchEditor.addMeasurementHeading", {
                        defaultValue: "Legg til ny måling",
                      })}
                    </label>
                    <div className="flex min-w-0 items-stretch gap-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        className={`min-w-0 flex-1 ${RV_INPUT} py-2.5 text-sm`}
                        placeholder={t("report.variables.sketchEditor.addMeasurementPlaceholder", {
                          defaultValue: "Verdi",
                        })}
                        value={newMeasurementValue}
                        onChange={(e) => setNewMeasurementValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddMeasurement();
                        }}
                      />
                      <button
                        type="button"
                        className={RV_BTN_OUTLINE_SM}
                        disabled={isSaving || !newMeasurementValue || Number.isNaN(parseFloat(newMeasurementValue))}
                        onClick={handleAddMeasurement}
                      >
                        {isSaving
                          ? t("report.variables.sketchEditor.addingMeasurement", { defaultValue: "Legger til…" })
                          : t("report.variables.sketchEditor.addMeasurementButton", { defaultValue: "Legg til" })}
                      </button>
                    </div>
                    {measurementUnit &&
                    newMeasurementValue &&
                    !Number.isNaN(parseFloat(String(newMeasurementValue).replace(",", "."))) ? (
                      <p className="text-xs text-gray-500">
                        {formatUnitDisplay(measurementUnit, parseFloat(String(newMeasurementValue).replace(",", ".")))}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <div className="mb-2">
                  <h3 className="mb-2 text-sm font-medium text-gray-900">
                    {t("report.variables.sketchEditor.historyHeading", {
                      defaultValue: "Målingshistorikk ({{count}})",
                      count: measurements.length,
                    })}
                  </h3>
                  {isLoadingHistory ? (
                    <p className="py-6 text-center text-sm text-gray-500">
                      {t("report.variables.sketchEditor.historyLoading", { defaultValue: "Laster målinger…" })}
                    </p>
                  ) : measurements.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-gray-200 py-6 text-center text-sm text-gray-500">
                      {t("report.variables.sketchEditor.historyEmpty", {
                        defaultValue: "Ingen målinger registrert",
                      })}
                    </p>
                  ) : (
                    <div className="max-h-56 overflow-y-auto rounded-2xl border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-100 text-sm">
                        <thead className="sticky top-0 bg-gray-50">
                          <tr>
                            <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                              {t("report.variables.sketchEditor.historyColValue", { defaultValue: "Verdi" })}
                            </th>
                            <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                              {t("report.variables.sketchEditor.historyColDate", { defaultValue: "Dato/tid" })}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {measurements.map((measurement, idx) => (
                            <tr
                              key={measurement.id}
                              className={idx === 0 ? "bg-gray-50/80" : "hover:bg-gray-50"}
                            >
                              <td className="whitespace-nowrap px-3 py-2.5 font-medium text-gray-900">
                                {measurementUnit
                                  ? formatUnitDisplay(measurementUnit, measurement.value)
                                  : `${parseFloat(measurement.value).toFixed(2)}`}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2.5 text-gray-500">
                                {formatDate(measurement.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                  <button type="button" className={RV_BTN_OUTLINE} onClick={onClose}>
                    {t("report.variables.sketchEditor.close", { defaultValue: "Lukk" })}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default VariableMeasurementDialog;
